import { create } from 'zustand';
import { cabinet } from '../../api/cabinet';
import { isApiError } from '../../api/client';
import type { ClientNotification } from '../../api/types';
import type { DictKey } from '../../i18n';

/**
 * Notifications the family got about their cases (GET /cabinet/notifications). Every message is also kept here,
 * even when no e-mail/messenger is connected (status `logged`). Until the backend has the endpoint (404) the list is
 * simply empty and the badge hidden.
 */
interface NotificationsState {
  items: ClientNotification[];
  unread: number;
  nextCursor: string | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: unknown;
  /** Ids unread at the moment the page was opened (shown as «Нове» while the page stays open). */
  fresh: string[];
  /** Badge only: refresh the unread counter. Quiet on any error. */
  refreshCount: () => Promise<void>;
  /** First page; with `markRead` also marks everything as read. */
  load: (o?: { markRead?: boolean }) => Promise<void>;
  more: () => Promise<void>;
  reset: () => void;
}

const quiet = (e: unknown) => isApiError(e) && (e.status === 404 || e.status === 405 || e.status === 501);

export const useNotifications = create<NotificationsState>((set, get) => ({
  items: [],
  unread: 0,
  nextCursor: null,
  status: 'idle',
  error: null,
  fresh: [],
  async refreshCount() {
    try {
      const r = await cabinet.notifications({ limit: 1 });
      set({ unread: Math.max(0, Number(r?.unread_count) || 0) });
    } catch {
      /* badge is a hint: never show an error for it */
    }
  },
  async load(o = {}) {
    set({ status: get().items.length ? 'ready' : 'loading', error: null });
    try {
      const r = await cabinet.notifications();
      const items = Array.isArray(r?.items) ? r.items : [];
      const unread = Math.max(0, Number(r?.unread_count) || 0);
      set({ items, nextCursor: r?.next_cursor ?? null, unread, status: 'ready', fresh: items.filter((i) => !i.read_at).map((i) => i.id) });
      if (o.markRead && (unread > 0 || items.some((i) => !i.read_at))) {
        try {
          await cabinet.markNotificationsRead();
          const now = new Date().toISOString();
          set((s) => ({ unread: 0, items: s.items.map((i) => (i.read_at ? i : { ...i, read_at: now })) }));
        } catch {
          /* stays unread; next visit tries again */
        }
      }
    } catch (e) {
      if (quiet(e)) set({ items: [], nextCursor: null, unread: 0, status: 'ready' });
      else set({ status: 'error', error: e });
    }
  },
  async more() {
    const cursor = get().nextCursor;
    if (!cursor) return;
    try {
      const r = await cabinet.notifications({ cursor });
      const have = new Set(get().items.map((i) => i.id));
      set((s) => ({ items: [...s.items, ...(r.items ?? []).filter((i) => !have.has(i.id))], nextCursor: r.next_cursor ?? null }));
    } catch (e) {
      set({ error: e });
    }
  },
  reset() {
    set({ items: [], unread: 0, nextCursor: null, status: 'idle', error: null, fresh: [] });
  },
}));

export interface DayGroup {
  /** Local calendar day, YYYY-MM-DD. */
  day: string;
  items: ClientNotification[];
}

function localDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Newest first, grouped by the local day of `created_at`. */
export function groupByDate(items: ClientNotification[]): DayGroup[] {
  const sorted = items.slice().sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
  const out: DayGroup[] = [];
  for (const it of sorted) {
    const day = localDay(it.created_at);
    const last = out[out.length - 1];
    if (last && last.day === day) last.items.push(it);
    else out.push({ day, items: [it] });
  }
  return out;
}

/** «Сьогодні» / «Учора» or null (then the date itself is shown). */
export function relativeDay(day: string, now: Date = new Date()): 'notif.today' | 'notif.yesterday' | null {
  if (day === localDay(now.toISOString())) return 'notif.today';
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  return day === localDay(y.toISOString()) ? 'notif.yesterday' : null;
}

const VIA: Record<string, DictKey> = {
  email: 'notif.via.email',
  sms: 'notif.via.sms',
  telegram: 'notif.via.telegram',
  viber: 'notif.via.viber',
  whatsapp: 'notif.via.whatsapp',
};

/**
 * How the message reached the family: «Надіслано на e-mail», «Надішлемо вранці» (quiet hours),
 * otherwise «Записано в кабінеті» (no channel connected, or delivery did not work — it is here anyway).
 */
export function deliveryKey(n: Pick<ClientNotification, 'channel' | 'status'>): DictKey {
  if (n.status === 'queued') return 'notif.queued';
  if (n.status === 'sent' && VIA[n.channel]) return VIA[n.channel]!;
  return 'notif.logged';
}
